import { useMemo, useState } from "react";
import { motion } from "framer-motion";
import { FadeIn } from "../components/FadeIn";
import { PortfolioLightbox } from "../components/PortfolioLightbox";
import { PORTFOLIO } from "../lib/images";
import { useT, usePageMeta } from "../lib/i18n";

export default function Portfolio() {
  const t = useT();
  usePageMeta({ titleKey: "meta.portfolio.title", descKey: "meta.portfolio.desc", path: "/portfolio" });

  const FILTERS = [
    { k: "all",      l: t("portfolio.filter.all") },
    { k: "paysage",  l: t("portfolio.filter.paysage") },
    { k: "sport",    l: t("portfolio.filter.sport") },
    { k: "aventure", l: t("portfolio.filter.aventure") },
  ];

  const [filter, setFilter] = useState("all");
  const [open, setOpen] = useState(null);

  const items = useMemo(
    () => (filter === "all" ? PORTFOLIO : PORTFOLIO.filter((p) => p.cat === filter)),
    [filter]
  );

  const next  = () => setOpen((i) => (i + 1) % items.length);
  const prev  = () => setOpen((i) => (i - 1 + items.length) % items.length);
  const close = () => setOpen(null);

  return (
    <main data-testid="portfolio-page" className="bg-ink text-bone min-h-screen pt-32">
      <section className="container-x pb-20">
        <FadeIn>
          <div className="eyebrow mb-4">{t("portfolio.eyebrow")}</div>
          <h1 className="font-display text-6xl md:text-9xl leading-[0.9] mb-12">
            {t("portfolio.headlineA")}{" "}
            <span className="italic text-stone">{t("portfolio.headlineB")}</span>
          </h1>
          <p className="max-w-xl text-bone/70 mb-12">{t("portfolio.intro")}</p>
        </FadeIn>

        <div className="flex flex-wrap gap-3 mb-12">
          {FILTERS.map((f) => (
            <button
              key={f.k}
              data-testid={`filter-${f.k}`}
              data-cursor=""
              onClick={() => { setFilter(f.k); setOpen(null); }}
              className={`chip ${filter === f.k ? "active" : ""}`}
            >
              {f.l}
            </button>
          ))}
        </div>

        <div className="columns-1 sm:columns-2 lg:columns-3 gap-4 space-y-4">
          {items.map((p, i) => (
            <motion.div
              key={p.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: (i % 9) * 0.06, ease: [0.22, 1, 0.36, 1] }}
              className="break-inside-avoid mb-4"
            >
              <button
                data-testid={`portfolio-item-${p.id}`}
                data-cursor="VOIR"
                onClick={() => setOpen(i)}
                className="block w-full overflow-hidden group text-left"
              >
                <div className="overflow-hidden bg-ink/40">
                  <img
                    src={p.src}
                    alt={p.title}
                    loading="lazy"
                    decoding="async"
                    className="w-full h-auto object-cover group-hover:scale-[1.04] transition-transform duration-[1200ms] ease-out opacity-0"
                    onLoad={(e) => e.currentTarget.classList.remove("opacity-0")}
                    style={{ transition: "opacity 500ms ease, transform 1200ms ease-out" }}
                  />
                </div>
                <div className="flex justify-between mt-3 text-[11px] tracking-[0.2em] uppercase text-bone/50">
                  <span>{p.title}</span>
                  <span>{t(`cat.${p.cat}`)}</span>
                </div>
              </button>
            </motion.div>
          ))}
        </div>
      </section>

      <PortfolioLightbox
        items={items}
        index={open}
        onClose={close}
        onPrev={prev}
        onNext={next}
      />
    </main>
  );
}
